import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Box, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import RefreshIcon from '@mui/icons-material/Refresh';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';

const actionButtonSx = {
    width: 28,
    height: 28,
    color: 'text.disabled',
    '&:hover': { bgcolor: 'action.hover', color: 'text.secondary' },
};

// Always-visible action row below a finalized assistant answer:
// references pill (passed in), copy, regenerate, 👍/👎 feedback,
// plus optional extra actions (e.g. export).
const MessageActionsRow = ({
    message,
    references,
    onRegenerate,
    onFeedback,
    disabled,
    extras,
}) => {
    const intl = useIntl();
    const [copied, setCopied] = useState(false);
    const copyTimerRef = useRef(null);

    useEffect(() => () => clearTimeout(copyTimerRef.current), []);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(message?.content || '');
            setCopied(true);
            clearTimeout(copyTimerRef.current);
            copyTimerRef.current = setTimeout(() => setCopied(false), 1000);
        } catch (err) {
            console.error('Clipboard copy failed:', err);
        }
    };

    const vote = message?.feedback;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                mt: 0.5,
                flexWrap: 'wrap',
            }}
        >
            {references}
            <Tooltip
                title={
                    copied
                        ? intl.formatMessage({ id: 'askPrompta.copied', defaultMessage: 'Copied' })
                        : intl.formatMessage({ id: 'askPrompta.copy_answer', defaultMessage: 'Copy answer' })
                }
            >
                <IconButton
                    size="small"
                    onClick={handleCopy}
                    aria-label={intl.formatMessage({ id: 'askPrompta.copy_answer', defaultMessage: 'Copy answer' })}
                    sx={actionButtonSx}
                >
                    {copied ? (
                        <CheckIcon sx={{ fontSize: 15, color: 'success.main' }} />
                    ) : (
                        <ContentCopyIcon sx={{ fontSize: 15 }} />
                    )}
                </IconButton>
            </Tooltip>
            {onRegenerate && (
                <Tooltip title={intl.formatMessage({ id: 'askPrompta.regenerate', defaultMessage: 'Regenerate' })}>
                    <span>
                        <IconButton
                            size="small"
                            onClick={onRegenerate}
                            disabled={disabled}
                            aria-label={intl.formatMessage({ id: 'askPrompta.regenerate', defaultMessage: 'Regenerate' })}
                            sx={actionButtonSx}
                        >
                            <RefreshIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            )}
            <Tooltip title={intl.formatMessage({ id: 'askPrompta.helpful', defaultMessage: 'Helpful' })}>
                <IconButton
                    size="small"
                    onClick={() => onFeedback?.('up')}
                    aria-label={intl.formatMessage({ id: 'askPrompta.helpful', defaultMessage: 'Helpful' })}
                    aria-pressed={vote === 'up'}
                    sx={actionButtonSx}
                >
                    {vote === 'up' ? (
                        <ThumbUpAltIcon sx={{ fontSize: 15, color: 'primary.main' }} />
                    ) : (
                        <ThumbUpAltOutlinedIcon sx={{ fontSize: 15 }} />
                    )}
                </IconButton>
            </Tooltip>
            <Tooltip title={intl.formatMessage({ id: 'askPrompta.not_helpful', defaultMessage: 'Not helpful' })}>
                <IconButton
                    size="small"
                    onClick={() => onFeedback?.('down')}
                    aria-label={intl.formatMessage({ id: 'askPrompta.not_helpful', defaultMessage: 'Not helpful' })}
                    aria-pressed={vote === 'down'}
                    sx={actionButtonSx}
                >
                    {vote === 'down' ? (
                        <ThumbDownAltIcon sx={{ fontSize: 15, color: 'primary.main' }} />
                    ) : (
                        <ThumbDownAltOutlinedIcon sx={{ fontSize: 15 }} />
                    )}
                </IconButton>
            </Tooltip>
            {extras}
        </Box>
    );
};

export default MessageActionsRow;
